import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFlowerTypeOptions, useAssemblyTypeOptions, useColorOptions } from '../../hooks/useProducts';
import { productFormSchema } from '../../utils/productValidation';
import { Button, ControlledInput, ControlledTextarea, ControlledFileInput, ControlledSelect, ControlledMultiSelect, ControlledCompositionInput } from '@shared/ui';
// import { ControlledKeywordsInput } from '../KeywordsInput';
import { ControlledCategorySelection, type CategorySelectionItem } from '../CategorySelection';
import { useCategoriesGroupedBySection } from '@modules/categories';
import type { CompositionItem } from '@shared/ui';
import './ProductForm.css';

interface ProductFormData {
  categorySelections?: CategorySelectionItem[];
  name: string;
  description: string;
  price: number;
  composition?: CompositionItem[];
  keywords?: string[];
  height?: number;
  width?: number;
  weight?: number;
  flowerCount?: number;
  isNotFlower?: boolean;
  flowerTypeIds?: number[];
  assemblyTypeId?: number | null;
  colorIds?: number[];
  images?: (File | { id: number; url: string })[];
}

interface ProductFormProps {
  onSubmit?: (data: ProductFormData) => void | Promise<void>;
  initialValues?: Partial<ProductFormData>;
  isEdit?: boolean;
  isLoading?: boolean
}

export default function ProductForm({ onSubmit: onSubmitProp, initialValues, isEdit = false, isLoading }: ProductFormProps = {}) {
  const resolver = yupResolver(productFormSchema) as any;

  const { handleSubmit, formState: { errors }, getValues, control, setValue, watch, clearErrors } = useForm<ProductFormData>({
    resolver,
    mode: 'onChange',
    defaultValues: initialValues || {
      categorySelections: [{ sectionId: '', categoryId: '' }],
      name: '',
      description: '',
      price: undefined,
      composition: [{ value: '', count: 0 }],
      keywords: [],
      height: undefined,
      width: undefined,
      weight: undefined,
      flowerCount: undefined,
      isNotFlower: false,
      flowerTypeIds: [],
      assemblyTypeId: null,
      colorIds: [],
      images: [],
      // currentImages: [],
    },
  });

  const flowerTypeOptions = useFlowerTypeOptions();
  const assemblyTypeOptions = useAssemblyTypeOptions();
  const colorOptions = useColorOptions();
  const { data: categoriesGroupedBySection = {} } = useCategoriesGroupedBySection();

  const isNotFlower = watch('isNotFlower') || false;
  const categorySelections = watch('categorySelections');
  const categoriesReady = Object.keys(categoriesGroupedBySection).length > 0;
  const notFlowerFromCategory = useMemo(() => {
    if (!categoriesReady) return isNotFlower;
    return categorySelectionsAreNotFlower(categorySelections, categoriesGroupedBySection);
  }, [categoriesReady, isNotFlower, categorySelections, categoriesGroupedBySection]);

  useEffect(() => {
    if (isNotFlower === notFlowerFromCategory) return;
    setValue('isNotFlower', notFlowerFromCategory);
  }, [isNotFlower, notFlowerFromCategory, setValue]);

  useEffect(() => {
    if (isNotFlower) {
      setValue('flowerTypeIds', []);
      setValue('assemblyTypeId', null);
      setValue('flowerCount', undefined);
      clearErrors(['flowerTypeIds', 'assemblyTypeId', 'colorIds']);
    }
  }, [isNotFlower, setValue, clearErrors]);

  const onSubmit = async (data: ProductFormData) => {
    if (onSubmitProp) {
      await onSubmitProp(data);
    }
  };

  const onError = (errors: any) => {
    const formData = getValues();
    console.log('Ошибки валидации:', errors);
    console.log('Данные формы (с ошибками):', formData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="product-form">
      <div className="product-form__columns">
        <div className="product-form__left">
          <div className="form-group">
            <ControlledCategorySelection
              name="categorySelections"
              control={control}
              error={errors.categorySelections as any}
            />
          </div>

          <div className="form-group">
            <ControlledInput
              id="name"
              type="text"
              label="Название *"
              name="name"
              control={control}
              error={errors.name}
            />
          </div>

          <div className="form-group">
            <ControlledTextarea
              id="description"
              rows={4}
              label="Описание"
              name="description"
              control={control}
              error={errors.description}
            />
          </div>

          <div className="form-group">
            <ControlledInput
              id="price"
              type="number"
              step="0.01"
              min="0"
              label="Цена *"
              name="price"
              control={control}
              error={errors.price}
            />
          </div>

          <div className="form-group">
            <ControlledCompositionInput
              name="composition"
              control={control}
              error={errors.composition as any}
              label="Состав"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <ControlledInput
                id="height"
                type="number"
                step="0.01"
                min="0"
                label="Высота (см)"
                name="height"
                control={control}
                error={errors.height}
              />
            </div>

            <div className="form-group">
              <ControlledInput
                id="width"
                type="number"
                step="0.01"
                min="0"
                label="Ширина (см)"
                name="width"
                control={control}
                error={errors.width}
              />
            </div>
          </div>

          <div className="form-group">
            <ControlledInput
              id="weight"
              type="number"
              step="1"
              min="0"
              label="Вес (г)"
              name="weight"
              control={control}
              error={errors.weight}
            />
          </div>
        </div>

        <div className="product-form__right">

          {!isNotFlower ? (
            <>
          <div className="form-group">
            <ControlledInput
              id="flowerCount"
              type="number"
              step="1"
              min="0"
              label="Количество цветов в букете"
              name="flowerCount"
              control={control}
              error={errors.flowerCount}
            />
          </div>

          <div className="form-group">
            <ControlledMultiSelect
              id="flowerTypeIds"
              label="Типы цветков"
              placeholder="Выберите типы цветков"
              data={flowerTypeOptions}
              name="flowerTypeIds"
              control={control}
              clearable
              searchable
              error={errors.flowerTypeIds as any}
            />
          </div>

          <div className="form-group">
            <ControlledSelect
              id="assemblyTypeId"
              label="Тип сборки"
              placeholder="Выберите тип сборки"
              name="assemblyTypeId"
              control={control}
              data={assemblyTypeOptions}
              clearable
              nothingFound="Ничего не найдено"
            />
          </div>
            </>
          ) : (
            <p className="bouquet-fields-hint">
              Категория без цветов: тип цветка и тип сборки не нужны. Цвет можно указать.
            </p>
          )}

          <div className="form-group">
            <ControlledMultiSelect
              id="colorIds"
              label={isNotFlower ? 'Цвет' : 'Цвета'}
              placeholder="Выберите цвет"
              data={colorOptions}
              name="colorIds"
              control={control}
              clearable
              searchable
              error={errors.colorIds as any}
            />
          </div>

          {/* Ключевые слова скрыты, поле остаётся необязательным. */}
          {/* <div className="form-group">
            <div className="keywords-input-wrapper">
              <ControlledKeywordsInput
                name="keywords"
                control={control}
              />
            </div>
          </div> */}

          <div className="form-group">
            <ControlledFileInput
              id="images"
              name="images"
              label="Изображения *(максимум 10)"
              accept="image/*"
              multiple
              control={control}
              error={errors.images}
            />
          </div>
        </div>
      </div>

      <footer className="product-form__footer">
        <Button
          type="submit"
          loading={isLoading}
          className="product-form__submit-button"
        >
          {isEdit ? 'Обновить' : 'Создать товар'}
        </Button>
      </footer>
    </form>
  );
}

function categorySelectionsAreNotFlower(
  selections: CategorySelectionItem[] | undefined,
  grouped: Record<string, Array<{ id: number; isNotFlower?: boolean }> | undefined>,
): boolean {
  const ids = (selections ?? [])
    .map((item) => item.categoryId)
    .filter((id): id is number => typeof id === 'number');
  if (ids.length === 0) return false;
  const flags = new Map<number, boolean>();
  Object.values(grouped).forEach((list) => {
    list?.forEach((category) => {
      flags.set(category.id, category.isNotFlower === true);
    });
  });
  return ids.every((id) => flags.get(id) === true);
}

